import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Divider,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd as RegisterIcon,
  Email as EmailIcon,
  Badge as RutIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/context";

// Utilidades para validación de RUT
const cleanRut = (rut) => {
  return rut.replace(/[^0-9kK]/g, "");
};

const formatRut = (rut) => {
  const cleaned = cleanRut(rut);
  if (cleaned.length < 2) return cleaned;

  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);

  // Formatear con puntos y guión
  let formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${formatted}-${dv}`;
};

const validateRut = (rut) => {
  const cleaned = cleanRut(rut);
  if (cleaned.length < 8 || cleaned.length > 9) return false;

  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1).toLowerCase();

  let sum = 0;
  let multiplier = 2;

  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  const calculatedDv =
    remainder < 2
      ? remainder.toString()
      : 11 - remainder === 10
      ? "k"
      : (11 - remainder).toString();

  return dv === calculatedDv;
};

const detectInputType = (value) => {
  const cleaned = value.trim();

  // Si contiene @ es email
  if (cleaned.includes("@")) return "email";

  // Si contiene solo números, puntos, guiones y k/K, es RUT
  if (/^[0-9\.\-kK]+$/.test(cleaned)) return "rut";

  // Por defecto, si no se puede determinar
  return "unknown";
};

const LoginForm = ({ open, onClose, onOpenRegister }) => {
  const [formData, setFormData] = useState({
    identifier: "", // Cambié de 'email' a 'identifier' para soportar ambos
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [inputType, setInputType] = useState("unknown"); // 'email', 'rut', 'unknown'
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "identifier") {
      // Detectar tipo de entrada y formatear si es RUT
      const type = detectInputType(value);
      setInputType(type);

      let formattedValue = value;
      if (type === "rut" && value.length > 1) {
        formattedValue = formatRut(value);
      }

      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Limpiar errores cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    setLoginError("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.identifier.trim()) {
      newErrors.identifier = "El email o RUT es requerido";
    } else {
      const type = detectInputType(formData.identifier);

      if (type === "email") {
        // Validar email
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(formData.identifier)) {
          newErrors.identifier = "Debes ingresar un email válido";
        }
      } else if (type === "rut") {
        // Validar RUT
        if (!validateRut(formData.identifier)) {
          newErrors.identifier = "Debes ingresar un RUT válido";
        }
      } else {
        newErrors.identifier =
          "Debes ingresar un email válido o un RUT chileno";
      }
    }

    if (!formData.password.trim()) {
      newErrors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setLoginError("");

    try {
      // Preparar datos para envío
      const loginData = {
        password: formData.password,
      };

      // Determinar si es email o RUT y agregar el campo correspondiente
      const type = detectInputType(formData.identifier);
      if (type === "email") {
        loginData.email = formData.identifier;
      } else if (type === "rut") {
        loginData.email = cleanRut(formData.identifier); // Enviar RUT limpio al backend
      }

      const result = await login(loginData);

      if (result.success) {
        console.log("✅ Login exitoso");
        handleClose();
      } else {
        setLoginError(result.error || "Credenciales inválidas");
      }
    } catch (error) {
      console.error("❌ Error en login:", error);
      setLoginError("Error de conexión. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ identifier: "", password: "" });
    setErrors({});
    setLoginError("");
    setShowPassword(false);
    setInputType("unknown");
    onClose();
  };

  const handleRegisterClick = () => {
    handleClose();
    onOpenRegister();
  };

  // Determinar placeholder y label según el tipo detectado
  const getFieldProps = () => {
    switch (inputType) {
      case "email":
        return {
          label: "Email",
          placeholder: "ejemplo@correo.com",
          type: "email",
          autoComplete: "email",
        };
      case "rut":
        return {
          label: "RUT",
          placeholder: "12.345.678-9",
          type: "text",
          autoComplete: "username",
        };
      default:
        return {
          label: "Email o RUT",
          placeholder: "ejemplo@correo.com o 12.345.678-9",
          type: "text",
          autoComplete: "username",
        };
    }
  };

  const fieldProps = getFieldProps();

  return (
    <Dialog
      disableScrollLock
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" component="h2">
            Iniciar Sesión
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Ingresa con tu email o RUT para acceder a todas las funcionalidades
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {loginError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loginError}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              name="identifier"
              label={fieldProps.label}
              placeholder={fieldProps.placeholder}
              type={fieldProps.type}
              value={formData.identifier}
              onChange={handleChange}
              error={!!errors.identifier}
              helperText={
                errors.identifier ||
                (inputType === "rut"
                  ? "Formato: 12.345.678-9"
                  : inputType === "email"
                  ? "Formato: usuario@dominio.com"
                  : "Puedes usar email o RUT chileno")
              }
              fullWidth
              required
              autoComplete={fieldProps.autoComplete}
            />

            <TextField
              name="password"
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Sección de registro */}
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              ¿No tienes cuenta?
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleRegisterClick}
              startIcon={<RegisterIcon />}
              sx={{ textTransform: "none" }}
            >
              Crear Cuenta Nueva
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Regístrate para acceder a todas las funcionalidades de la
              plataforma
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<LoginIcon />}
            sx={{ minWidth: 140 }}
          >
            {loading ? "Iniciando..." : "Iniciar Sesión"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LoginForm;
